import { ExistingCollectionObservationForm } from "@/components/observations/ExistingCollectionObservationForm"
import { NewCollectionObservationForm } from "@/components/observations/NewCollectionObservationForm"
import type {
  ExistingSpeciesValue,
  SpeciesValue,
} from "@/components/observations/SelectedSpeciesItem"
import { toAlaKingdomOrNull } from "@fieldbook/common/utils"
import { SpeciesObservationForm } from "@/components/observations/SpeciesObservationForm"
import { useCollection } from "@/hooks/useCollection"
import { useObservation } from "@/hooks/useObservation"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import { useSpecies } from "@/hooks/useSpecies"
import { Spinner } from "@fieldbook/ui/spinner"
import { createFileRoute } from "@tanstack/react-router"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import * as z from "zod"

const searchSchema = z.object({
  draftId: z.string().uuid().optional(),
})

export const Route = createFileRoute(
  "/_private/projects/$id/observations/$observationId/edit",
)({
  component: EditObservation,
  validateSearch: (search) => searchSchema.parse(search),
})

function EditObservation() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId, observationId } = Route.useParams()
  const { draftId } = Route.useSearch()

  const { data: observation, isPending: isObsPending } = useObservation({
    observationId,
    projectId,
  })

  const { data: species } = useSpecies(observation?.species_id ?? undefined)

  const { data: collection } = useCollection({
    collectionId: observation?.collection_id ?? undefined,
    projectId,
  })

  const { data: photos } = usePhotosForEntity({
    entityId: observationId,
    entityType: "observation",
  })

  if (isObsPending || !observation) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  // New collection observation: is_collection flag is true
  if (observation.is_collection) {
    return (
      <NewCollectionObservationForm
        projectId={projectId}
        siteId={observation.survey_site_id ?? undefined}
        instance={observation}
        instanceCollection={collection}
        initialPhotos={photos}
        draftId={draftId}
      />
    )
  }

  // Existing collection observation: has collection_id but is_collection is false
  if (observation.collection_id) {
    return (
      <ExistingCollectionObservationForm
        projectId={projectId}
        siteId={observation.survey_site_id ?? undefined}
        collectionId={observation.collection_id}
        instance={observation}
        initialPhotos={photos}
        draftId={draftId}
      />
    )
  }

  // Species observation (default)
  const speciesValue: SpeciesValue | undefined = species
    ? ({
        type: "existing",
        speciesId: species.id,
        name: species.name,
        alaGuid: species.ala_guid,
        kingdom: toAlaKingdomOrNull(species.kingdom),
      } satisfies ExistingSpeciesValue)
    : undefined

  return (
    <SpeciesObservationForm
      projectId={projectId}
      siteId={observation.survey_site_id ?? undefined}
      speciesId={observation.species_id ?? undefined}
      speciesValue={speciesValue}
      instance={observation}
      initialPhotos={photos}
      draftId={draftId}
    />
  )
}
